import { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { fmtH } from "../lib/planning";
import {
    CalendarDays, ChevronLeft, ChevronRight, Loader2, AlertTriangle, Zap, Printer,
} from "lucide-react";

const DAY_SHORT = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

const toIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const isoAddDays = (iso, n) => {
    const d = new Date(`${iso}T12:00:00`);
    d.setDate(d.getDate() + n);
    return toIso(d);
};
const mondayOf = (d = new Date()) => {
    const x = new Date(d);
    x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
    return toIso(x);
};
const fmtDay = (iso) => {
    try {
        return new Date(`${iso}T00:00:00`).toLocaleDateString("it-IT", { day: "numeric", month: "short" });
    } catch { return iso; }
};
const fmtRange = (days) => {
    if (!days?.length) return "";
    const a = new Date(`${days[0]}T00:00:00`);
    const b = new Date(`${days[6]}T00:00:00`);
    return `${a.toLocaleDateString("it-IT", { day: "numeric", month: "long" })} – ${b.toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}`;
};

export default function PianificazionePage() {
    const [start, setStart] = useState(mondayOf());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);

    const exportPdf = async () => {
        setExporting(true);
        try {
            const { data: blob } = await api.get("/presenze/planning/week.pdf", {
                params: { start }, responseType: "blob",
            });
            const url = URL.createObjectURL(new Blob([blob]));
            const a = document.createElement("a");
            a.href = url;
            a.download = `piano-settimanale-${start}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            toast.error("Errore nella generazione del PDF");
        } finally { setExporting(false); }
    };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/presenze/planning/week", { params: { start } });
            setData(data);
        } catch {
            toast.error("Errore nel caricamento della pianificazione");
        } finally { setLoading(false); }
    }, [start]);

    useEffect(() => { load(); }, [load]);

    const todayIso = toIso(new Date());

    return (
        <div className="mx-auto max-w-7xl px-6 py-8" data-testid="pianificazione-page">
            <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-start gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-md bg-[#376DB3]/10 text-[#376DB3]">
                        <CalendarDays className="h-6 w-6" />
                    </div>
                    <div>
                        <h1 className="font-display text-2xl font-bold text-slate-800">Pianificazione settimanale</h1>
                        <p className="text-sm text-slate-500">Chi lavora dove, assenze e turni da coprire</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {data?.gaps > 0 && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-700" data-testid="pianif-gaps-badge">
                            <AlertTriangle className="h-3.5 w-3.5" /> {data.gaps} turni scoperti
                        </span>
                    )}
                    <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" onClick={exportPdf} disabled={exporting || loading} data-testid="pianif-pdf-btn">
                        {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Printer className="h-3.5 w-3.5" />}
                        Stampa PDF
                    </Button>
                    <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setStart(isoAddDays(start, -7))} data-testid="pianif-prev">
                        <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setStart(mondayOf())} data-testid="pianif-today">
                        Questa settimana
                    </Button>
                    <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setStart(isoAddDays(start, 7))} data-testid="pianif-next">
                        <ChevronRight className="h-4 w-4" />
                    </Button>
                </div>
            </div>

            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                <div className="font-display text-sm font-bold capitalize text-slate-700" data-testid="pianif-week-label">
                    {data ? fmtRange(data.days) : "…"}
                </div>
                <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-500">
                    <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-emerald-400" /> In servizio</span>
                    <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-red-400" /> Assente</span>
                    <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-sky-400" /> Sostituto</span>
                    <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-amber-400" /> Scoperto</span>
                </div>
            </div>

            {loading || !data ? (
                <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>
            ) : data.worksites.length === 0 ? (
                <div className="rounded-lg border border-dashed border-slate-200 bg-white p-12 text-center text-sm text-slate-500" data-testid="pianif-empty">
                    Nessuna attività pianificata: crea attività e turni dai cantieri.
                </div>
            ) : (
                data.worksites.map((ws) => (
                    <div key={ws.id} className="mb-6 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm" data-testid={`pianif-ws-${ws.id}`}>
                        <div className="border-b border-slate-100 bg-slate-50/70 px-4 py-2.5 font-display text-sm font-bold text-slate-700">
                            {ws.name}
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-xs">
                                <thead>
                                    <tr className="border-b border-slate-100 text-[10px] font-bold uppercase tracking-wide text-slate-400">
                                        <th className="w-52 px-3 py-2">Attività / turno</th>
                                        {data.days.map((d, i) => (
                                            <th key={d} className={`px-2 py-2 text-center ${d === todayIso ? "bg-[#376DB3]/5 text-[#376DB3]" : ""}`}>
                                                {DAY_SHORT[i]} <span className="font-normal">{fmtDay(d)}</span>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {ws.activities.map((act) => act.shifts.map((s) => (
                                        <tr key={s.id} data-testid={`pianif-shift-${s.id}`}>
                                            <td className="px-3 py-2 align-top">
                                                <div className="font-semibold text-slate-700">{act.name}</div>
                                                <div className="font-mono-numbers text-[11px] text-slate-400">{s.start}–{s.end}</div>
                                                {s.frequency_label && s.frequency_label !== "settimanale" && (
                                                    <div className="text-[10px] font-semibold text-violet-600" data-testid={`pianif-freq-${s.id}`}>{s.frequency_label}</div>
                                                )}
                                            </td>
                                            {data.days.map((d) => {
                                                const cell = s.cells[d];
                                                if (!cell) {
                                                    return <td key={d} className={`px-2 py-2 text-center text-slate-200 ${d === todayIso ? "bg-[#376DB3]/5" : ""}`}>—</td>;
                                                }
                                                return (
                                                    <td key={d} className={`px-1.5 py-1.5 align-top ${cell.gap ? "bg-amber-50" : d === todayIso ? "bg-[#376DB3]/5" : ""}`}>
                                                        <div className="flex flex-col items-stretch gap-1">
                                                            {cell.people.map((p, j) => (
                                                                <span key={`${p.employee_id}-${j}`}
                                                                    title={p.status === "sostituto" ? `Sostituisce ${p.sub_for}${p.reason ? ` (${p.reason})` : ""}` : p.status === "assente" ? `Assente: ${p.reason || "—"}` : undefined}
                                                                    className={`truncate rounded px-1.5 py-0.5 text-center text-[10px] font-semibold ${
                                                                        p.status === "ok" ? "bg-emerald-50 text-emerald-700"
                                                                            : p.status === "assente" ? "bg-red-50 text-red-500"
                                                                                : "bg-sky-50 text-sky-700"
                                                                    }`}>
                                                                    <span className={p.status === "assente" ? "line-through" : ""}>{p.name}</span>
                                                                    {p.status === "assente" && p.reason && <span className="ml-1 no-underline">· {p.reason}</span>}
                                                                </span>
                                                            ))}
                                                            {cell.gap && (
                                                                <span className="rounded bg-amber-100 px-1.5 py-0.5 text-center text-[10px] font-bold text-amber-700" data-testid={`pianif-gap-${s.id}-${d}`}>
                                                                    SCOPERTO
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    )))}
                                    {Object.keys(ws.extras || {}).length > 0 && (
                                        <tr className="bg-violet-50/40" data-testid={`pianif-extras-${ws.id}`}>
                                            <td className="px-3 py-2 align-top">
                                                <div className="flex items-center gap-1 font-semibold text-violet-700">
                                                    <Zap className="h-3.5 w-3.5" /> Straordinari
                                                </div>
                                            </td>
                                            {data.days.map((d) => (
                                                <td key={d} className="px-1.5 py-1.5 align-top">
                                                    <div className="flex flex-col items-stretch gap-1">
                                                        {(ws.extras[d] || []).map((x) => (
                                                            <span key={x.id} title={`${x.description} (${x.status})`}
                                                                className="truncate rounded bg-violet-100 px-1.5 py-0.5 text-center text-[10px] font-semibold text-violet-700">
                                                                {x.employee_name} · {fmtH(x.hours)}h
                                                            </span>
                                                        ))}
                                                    </div>
                                                </td>
                                            ))}
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ))
            )}
        </div>
    );
}
